import { memo, useState, useCallback } from 'react';
import { Handle, Position } from 'reactflow';
import { Film, X, Upload, CheckCircle, Play, Loader2, AlertTriangle, RotateCcw, Camera, Scaling } from 'lucide-react';
import NodeLimitWarning from './NodeLimitWarning';
import VideoAdaptModal from '../camera/VideoAdaptModal';
import { useWorkflowStore } from '../../store/workflowStore';
import { setSourcePickerHandler } from '../../utils/sourcePickerBridge';
import { uploadToR2 } from '../../utils/uploadToR2';
import { describeUploadError, limitMB } from '../../utils/uploadLimits';
import { useUploadLimits } from '../../hooks/useUploadLimits';
import { toast } from 'sonner';

const UploadVideoNode = memo(({ id, data, selected }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [adapt, setAdapt] = useState(null);   // AP-14 · {preset} con el modal abierto
  const updateNodeData = useWorkflowStore((state) => state.updateNodeData);
  const deleteNode = useWorkflowStore((state) => state.deleteNode);
  const openSourcePicker = useWorkflowStore((state) => state.openSourcePicker);
  const addAsset = useWorkflowStore((state) => state.addAsset);
  const uploadLimits = useUploadLimits();
  const maxVideoMb = limitMB(uploadLimits, 'video');

  const handleFile = useCallback((file) => {
    if (!file) return;

    const validTypes = ['video/mp4', 'video/mov', 'video/webm', 'video/quicktime'];
    if (!validTypes.some(t => file.type.includes(t.split('/')[1]))) {
      toast.error('Formato no admitido. Usá MP4, MOV o WebM.', { position: 'bottom-right' });
      return;
    }

    // 1) Show a local blob preview (in-memory only; will be replaced by R2 url)
    const localPreview = URL.createObjectURL(file);
    updateNodeData(id, {
      file,
      preview: localPreview,
      fileName: file.name,
      hasOutput: false,
      outputUrl: null,
      outputType: 'video',
      uploading: true,
      uploadError: null,
    });

    // 2) Upload to R2 in background
    setUploadProgress(0);
    uploadToR2(file, { onProgress: setUploadProgress })
      .then(({ url, r2Key, filename }) => {
        try { URL.revokeObjectURL(localPreview); } catch { /* ignore */ }
        updateNodeData(id, {
          preview: url,
          hasOutput: true,
          outputUrl: url,
          r2Key,
          uploading: false,
          uploaded: true,
          uploadError: null,
        });
        addAsset?.({
          id: `asset_${Date.now()}`,
          type: 'video',
          tool: 'upload',
          filename: filename || file.name,
          url,
          r2Key,
          createdAt: new Date().toISOString(),
        });
      })
      .catch((err) => {
        console.error('R2 upload failed (video):', err);
        const message = describeUploadError(err);
        toast.error(message, { position: 'bottom-right', duration: 8000 });
        updateNodeData(id, { uploading: false, uploadError: message });
      })
      .finally(() => setUploadProgress(null));
  }, [id, updateNodeData, addAsset]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
  }, []);

  const handleDragLeave = useCallback(() => {
    setDragActive(false);
  }, []);

  const clearVideo = useCallback(() => {
    if (data.preview && data.preview.startsWith('blob:')) {
      try { URL.revokeObjectURL(data.preview); } catch { /* ignore */ }
    }
    updateNodeData(id, {
      file: null,
      preview: null,
      fileName: null,
      hasOutput: false,
      outputUrl: null,
      outputType: null,
      r2Key: null,
      uploading: false,
      uploaded: false,
      uploadError: null,
    });
    setUploadProgress(null);
  }, [id, data.preview, updateNodeData]);

  const handleSourcePick = useCallback(() => {
    setSourcePickerHandler((result) => {
      if (result.file) {
        handleFile(result.file);
      } else if (result.url) {
        updateNodeData(id, {
          file: null,
          preview: result.url,
          fileName: result.fileName,
          hasOutput: true,
          outputUrl: result.url,
          outputType: 'video',
          fromGallery: !result.fromCamera,
          fromCamera: !!result.fromCamera,
          uploaded: !!result.fromCamera,
          uploadError: null,
        });
      }
    });
    openSourcePicker(id, ['video'], 'video/mp4,video/mov,video/webm');
  }, [id, openSourcePicker, handleFile, updateNodeData]);

  return (
    <div className={`node-container upload-video-node ${selected ? 'selected' : ''}`} data-testid="upload-video-node">
      {/* Header */}
      <div className="node-header upload-video-header">
        <div className="node-header-left">
          <Film size={16} />
          <span>Subir Video</span>
        </div>
        <button className="node-delete-btn" onClick={() => deleteNode(id)}>
          <X size={14} />
        </button>
      </div>
      
      {/* Content */}
      <div className="node-content">
        {data.preview ? (
          <div className="upload-video-preview">
            <video 
              src={data.preview} 
              className="upload-video-thumb"
              muted
            />
            <div className="upload-video-overlay">
              <Play size={20} />
            </div>
            <button className="upload-clear-btn" onClick={clearVideo}>
              <X size={12} />
            </button>
            {data.outputUrl && !data.uploading && (
              <button type="button" className="upload-adapt-btn nodrag" onClick={() => setAdapt({ preset: null })}
                title="Recortar, bajar resolución o peso (crea un video nuevo)" data-testid="upload-video-adapt">
                <Scaling size={11} /> Adaptar
              </button>
            )}
            <div className="upload-video-info">
              {data.uploading ? (
                <>
                  <Loader2 size={10} className="spin-icon" />
                  <span>{uploadProgress != null ? `${uploadProgress}%` : 'Subiendo...'}</span>
                </>
              ) : data.uploadError ? (
                <span className="upload-error-line" role="alert" title={data.uploadError} data-testid="upload-video-error">
                  <AlertTriangle size={10} />
                  <span>No se pudo subir</span>
                  {data.file && (
                    <button type="button" className="upload-retry-btn nodrag" onClick={() => handleFile(data.file)} data-testid="upload-video-retry">
                      <RotateCcw size={10} /> Reintentar
                    </button>
                  )}
                </span>
              ) : (
                <>
                  <CheckCircle size={10} />
                  <span>{data.fileName?.slice(0, 15)}...</span>
                </>
              )}
            </div>
          </div>
        ) : (
          <div
            className={`upload-video-dropzone ${dragActive ? 'active' : ''}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <Film size={24} className="upload-video-icon" />
            <span className="upload-video-text">Arrastra un video o</span>
            <span className="upload-video-hint">MP4, MOV, WebM{maxVideoMb ? ` · hasta ${maxVideoMb} MB` : ''}</span>
            <span className="node-load-row">
              <button type="button" className="node-load-btn" onClick={handleSourcePick} data-testid="load-btn-upload-video">
                <Upload size={14} /> Cargar
              </button>
              {/* AP-11 · grabar con la cámara (recorte + destino listo) */}
              <button type="button" className="node-load-btn nodrag" onClick={() => { handleSourcePick(); useWorkflowStore.getState().setSourcePickerStep('camera'); }}
                title="Grabar un clip con la cámara" data-testid="camera-btn-upload-video">
                <Camera size={14} /> Cámara
              </button>
            </span>
          </div>
        )}
      </div>

      {/* AP-14 · aviso si no cumple los límites del modelo conectado */}
      {data.outputUrl && !data.uploading && (
        <div className="node-limit-wrap">
          <NodeLimitWarning nodeId={id} url={data.outputUrl} kind="video" onAdapt={(preset) => setAdapt({ preset })} />
        </div>
      )}
      {adapt && (
        <VideoAdaptModal
          source={{ url: data.outputUrl, label: data.fileName || 'Video' }}
          initialPreset={adapt.preset}
          onClose={() => setAdapt(null)}
          onDone={(out) => updateNodeData(id, {
            preview: out.url, outputUrl: out.url, fileName: out.original_name, hasOutput: true,
            outputType: 'video', file: null, uploadError: null,
          })}
        />
      )}

      {/* Output Handle */}
      <Handle
        type="source"
        position={Position.Right}
        id="output"
        className="node-handle output-handle"
        isConnectable={!!data.hasOutput}
      />
    </div>
  );
});

UploadVideoNode.displayName = 'UploadVideoNode';

export default UploadVideoNode;
